import { useEffect, useState } from "react";

function secondsUntil(deadline: number): number {
	return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

/**
 * Wall-clock countdown to a target deadline.
 * Recomputes the remaining whole seconds from the deadline on every tick (chained
 * setTimeout, not setInterval), so it self-corrects after the component was unmounted
 * (e.g. the cart drawer was closed) or after timer drift. A `null` deadline means the
 * countdown has not started yet and yields `null`.
 * @param deadline Target epoch-millisecond timestamp, or `null` when not started.
 * @returns Remaining whole seconds floored at 0, or `null` when `deadline` is `null`.
 */
export function useCountdown(deadline: number | null): number | null {
	const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
		deadline === null ? null : secondsUntil(deadline),
	);
	useEffect(() => {
		if (deadline === null) {
			setSecondsLeft(null);
			return;
		}
		setSecondsLeft(secondsUntil(deadline));
	}, [deadline]);
	useEffect(() => {
		if (deadline === null || secondsLeft === null || secondsLeft <= 0) {
			return;
		}
		const timer = setTimeout(() => {
			setSecondsLeft(secondsUntil(deadline));
		}, 1000);
		return () => clearTimeout(timer);
	}, [deadline, secondsLeft]);
	return secondsLeft;
}
